import { hasValue } from "./data";
import { ETAPA_EXECUTIVO, etapaDoExecutivo, limiteDaRevisao } from "./fluxoExecutivo";
import { F } from "./gerencialFields";
import { getIndustrializacaoDeadline } from "./industrializacao";
import { getMarcenariaDeadline } from "./marcenaria";
import { getDeadlineStatusVariant } from "./status";

export const CLIENTE_STATUS_FLOW = [
  "Aguardando libera\u00E7\u00E3o",
  "Aguardando defini\u00E7\u00F5es",
  "Aguardando Medi\u00E7\u00E3o",
  "Aguardando Projeto Executivo",
  // Revis\u00E3o antes da aprova\u00E7\u00E3o (ordem nova, 28/10/2026). Linhas da ordem
  // antiga ainda passam pela revis\u00E3o depois da aprova\u00E7\u00E3o.
  "Aguardando Revis\u00E3o",
  "Aguardando Aprova\u00E7\u00E3o",
  "Aguardando Otimiza\u00E7\u00E3o de Lote",
  "Aguardando Compras",
  "Aguardando Corte",
  "Aguardando Bordo",
  "Aguardando Usinagem",
  "Aguardando In\u00EDcio de Marcenaria",
  "Aguardando Finalizar Marcenaria",
  "Aguardando Fornecedores Terceiros",
  "Aguardando Acabamentos",
  "Aguardando Entrega",
  "Entregue",
];

const CLIENTE_STATUS_TONES = {
  "Aguardando libera\u00E7\u00E3o": "info",
  "Aguardando defini\u00E7\u00F5es": "info",
  "Aguardando Medi\u00E7\u00E3o": "info",
  "Aguardando Projeto Executivo": "info",
  "Aguardando Aprova\u00E7\u00E3o": "info",
  "Aguardando Revis\u00E3o": "info",
  "Aguardando Otimiza\u00E7\u00E3o de Lote": "info",
  "Aguardando Compras": "purple",
  "Aguardando Corte": "lime",
  "Aguardando Bordo": "lime",
  "Aguardando Usinagem": "lime",
  "Aguardando In\u00EDcio de Marcenaria": "orange",
  "Aguardando Finalizar Marcenaria": "orange",
  "Aguardando Fornecedores Terceiros": "warning",
  "Aguardando Acabamentos": "warning",
  "Aguardando Entrega": "success",
  Entregue: "neutral",
};

const TERCEIROS_FIELDS = [
  F.SERRALHERIA_TERCEIROS,
  F.PERFIL_ALUMINIO_TERCEIROS,
  F.PORTAS_FRENTES_TERCEIROS,
  F.VIDRACARIA,
  F.TAPECARIA_TERCEIROS,
  F.FERRAGENS_ESPECIAIS_TERCEIROS,
];

const ACABAMENTO_FIELDS = [
  F.PINTURA,
  F.VERNIZ_ACAB,
  F.PINTURA_PERFIL,
  F.PINTURA_SERRALHERIA,
  F.ACABAMENTO_REALIZADO,
];

function getStageTone(deadline, { fallback = "default", referenceDate = new Date() } = {}) {
  if (!hasValue(deadline)) {
    return fallback;
  }

  return getDeadlineStatusVariant(deadline, { fallback: "success", referenceDate });
}

function buildClienteStatus(
  status,
  deadline = null,
  statusVariant = CLIENTE_STATUS_TONES[status] ?? "default",
  pendencias = []
) {
  return {
    status,
    statusVariant,
    deadline,
    // Etapas paralelas (terceiros, acabamentos) que ficaram para tras de um
    // ambiente ja ENTREGUE: {status, statusVariant} de cada uma. Vazio nos
    // demais casos — ali a pendencia e o proprio status.
    pendencias,
  };
}

export const STATUS_TERCEIROS = "Aguardando Fornecedores Terceiros";
export const STATUS_ACABAMENTOS = "Aguardando Acabamentos";
export const STATUS_ENTREGUE = "Entregue";

/** Terceiros e acabamentos ainda abertos, na ordem em que a tela os nomeia. */
function pendenciasParalelas(row) {
  const lista = [];
  if (hasPendingSimpleNumber(row, TERCEIROS_FIELDS)) lista.push(STATUS_TERCEIROS);
  if (hasPendingSimpleNumber(row, ACABAMENTO_FIELDS)) lista.push(STATUS_ACABAMENTOS);
  return lista.map((status) => ({ status, statusVariant: CLIENTE_STATUS_TONES[status] }));
}

export function isMeaningfulIdentifier(value) {
  if (!hasValue(value)) {
    return false;
  }

  return !["0", "0.0", "0,0"].includes(String(value).trim());
}

export function getPedidoIdentity(row, fallbackIndex = 0) {
  const pedido = row[F.NUMERO_DO_PEDIDO];
  const projeto = row[F.NUMERO_DO_PROJETO];
  const lote = row[F.NUMERO_DO_LOTE];
  const ambiente = String(row[F.AMBIENTE] ?? "").trim() || String(fallbackIndex + 1);

  if (isMeaningfulIdentifier(pedido)) {
    const value = String(pedido).trim();
    return {
      key: `pedido:${value}`,
      label: `Pedido ${value}`,
      type: "pedido",
      value,
    };
  }

  if (isMeaningfulIdentifier(projeto)) {
    const value = String(projeto).trim();
    return {
      key: `projeto:${value}`,
      label: `Projeto ${value}`,
      type: "projeto",
      value,
    };
  }

  if (isMeaningfulIdentifier(lote)) {
    const value = String(lote).trim();
    return {
      key: `lote:${value}`,
      label: `Lote ${value}`,
      type: "lote",
      value,
    };
  }

  return {
    key: `ambiente:${ambiente}:${fallbackIndex}`,
    label: "Pedido n\u00E3o informado",
    type: "fallback",
    value: null,
  };
}

export function getEntregaDeadline(row) {
  return row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA] || null;
}

function isPlainNumberNotDate(value) {
  if (!hasValue(value) || value instanceof Date) {
    return false;
  }

  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 && value < 1000;
  }

  const text = String(value).trim();
  if (!/^\d+$/.test(text)) {
    return false;
  }

  const numberValue = Number(text);
  return Number.isInteger(numberValue) && numberValue > 0 && numberValue < 1000;
}

function hasPendingSimpleNumber(row, fields) {
  return fields.some((field) => isPlainNumberNotDate(row[field]));
}

export function getAmbienteCurrentStatus(row, { referenceDate = new Date() } = {}) {
  const entregaDeadline = getEntregaDeadline(row);
  const industrializacaoDeadline = getIndustrializacaoDeadline(row);
  const marcenariaDeadline = getMarcenariaDeadline(row);

  if (!hasValue(row[F.LIBERACAO_DA_OBRA])) {
    return buildClienteStatus("Aguardando libera\u00E7\u00E3o", entregaDeadline);
  }

  if (!hasValue(row[F.DEFINICAO_ACABAMENTOS_REALIZADO])) {
    return buildClienteStatus("Aguardando defini\u00E7\u00F5es", row[F.DEFINICAO_ACABAMENTOS_LIMITE]);
  }

  if (!hasValue(row[F.MEDICAO_REALIZADO])) {
    return buildClienteStatus("Aguardando Medi\u00E7\u00E3o", row[F.MEDICAO_LIMITE]);
  }

  if (!hasValue(row[F.EXECUTIVO_REALIZADO])) {
    return buildClienteStatus("Aguardando Projeto Executivo", row[F.EXECUTIVO_LIMITE]);
  }

  // Revis\u00E3o e aprova\u00E7\u00E3o: a ordem depende da linha (utils/fluxoExecutivo).
  // OTIMIZA\u00C7\u00C3O preenchida sem REVIS\u00C3O digitada conta como revis\u00E3o impl\u00EDcita
  // (pedidos antigos). A revis\u00E3o da ordem antiga n\u00E3o tinha limite e herda a
  // entrega.
  const etapaExecutivo = etapaDoExecutivo(row);
  if (etapaExecutivo === ETAPA_EXECUTIVO.REVISAO) {
    return buildClienteStatus("Aguardando Revis\u00E3o", limiteDaRevisao(row) || entregaDeadline);
  }

  if (etapaExecutivo === ETAPA_EXECUTIVO.APROVACAO) {
    return buildClienteStatus("Aguardando Aprova\u00E7\u00E3o", row[F.APROVACAO_LIMITE]);
  }

  if (!hasValue(row[F.OTIMIZACAO_DO_LOTE])) {
    return buildClienteStatus("Aguardando Otimiza\u00E7\u00E3o de Lote", entregaDeadline);
  }

  if (!hasValue(row[F.MDF_BORDO])) {
    return buildClienteStatus("Aguardando Compras", entregaDeadline);
  }

  if (!hasValue(row[F.DATA_DE_CORTE])) {
    return buildClienteStatus("Aguardando Corte", industrializacaoDeadline);
  }

  if (!hasValue(row[F.DATA_DE_BORDO])) {
    return buildClienteStatus("Aguardando Bordo", industrializacaoDeadline);
  }

  if (!hasValue(row[F.DATA_DE_FURACAO]) || !hasValue(row[F.DATA_DE_USINAGEM])) {
    return buildClienteStatus("Aguardando Usinagem", industrializacaoDeadline);
  }

  if (!hasValue(row[F.MARCENARIA_INICIO])) {
    return buildClienteStatus("Aguardando In\u00EDcio de Marcenaria", marcenariaDeadline);
  }

  // MARCENARIA_TERMINO (BW) é a data real digitada pelo operador.
  // FINAL_MARCENARIA (BX) é fórmula WORKDAY de previsão — preenchida
  // automaticamente desde a aprovação do executivo, NÃO pode ser usada
  // aqui (mesmo padrão de bug que CO/ENTREGA_CLIENTE).
  if (!hasValue(row[F.MARCENARIA_TERMINO])) {
    return buildClienteStatus("Aguardando Finalizar Marcenaria", marcenariaDeadline);
  }

  // Decisão de "Entregue" usa F.CARREGAMENTO (CN). A coluna F.ENTREGA_CLIENTE
  // (CO) é fórmula WORKDAY de previsão — fica preenchida automaticamente
  // desde a aprovação e NÃO representa entrega real. Os módulos TOC já
  // evitam CO; aqui alinhamos a página Clientes com a mesma convenção.
  //
  // Entregue vence terceiros e acabamentos: o ambiente JA FOI para a obra e
  // o que falta e paralelo (vidro, pintura...). Antes a linha dizia so
  // "Aguardando Fornecedores Terceiros" e nao dava para saber que o movel
  // estava na casa do cliente (pedido do Igor, 05/10/2026). A pendencia nao
  // some: vai em `pendencias`, e a tela mostra os dois selos.
  const pendencias = pendenciasParalelas(row);
  if (hasValue(row[F.CARREGAMENTO])) {
    return buildClienteStatus(STATUS_ENTREGUE, row[F.CARREGAMENTO], undefined, pendencias);
  }

  if (pendencias.length) {
    return buildClienteStatus(pendencias[0].status, entregaDeadline);
  }

  if (!hasValue(row[F.CARREGAMENTO])) {
    return buildClienteStatus(
      "Aguardando Entrega",
      entregaDeadline,
      getStageTone(entregaDeadline, { fallback: "success", referenceDate })
    );
  }

  return buildClienteStatus(STATUS_ENTREGUE, row[F.CARREGAMENTO]);
}

/** Status principal + pendências paralelas, como a tela escreve ("Entregue + Aguardando …"). */
export function descreverStatus(info) {
  if (!info) return "";
  return [info.status, ...(info.pendencias || []).map((p) => p.status)].join(" + ");
}
